/**
 * All company content — mission, vision, values, process, differentiators.
 * No company content lives inside components.
 */

export const missionVision = {
  mission:
    'To deliver world-class software solutions that empower Ethiopian businesses and compete on a global stage.',
  vision:
    'To be the most trusted technology partner in East Africa — known for quality, integrity, and innovation.',
} as const

export type Value = {
  readonly id: string
  readonly title: string
  readonly description: string
}

export const companyValues: readonly Value[] = [
  {
    id: 'excellence',
    title: 'Excellence',
    description:
      'We do not ship mediocre work. Every line of code, every pixel, every decision reflects a commitment to quality.',
  },
  {
    id: 'transparency',
    title: 'Transparency',
    description:
      'You are never in the dark about your project. We communicate clearly, honestly, and proactively.',
  },
  {
    id: 'ownership',
    title: 'Ownership',
    description:
      'We take full responsibility for what we build. Problems are ours to solve, not yours to manage.',
  },
  {
    id: 'innovation',
    title: 'Innovation',
    description:
      'We stay ahead so you do not have to. We adopt the right technologies, not just the trendy ones.',
  },
] as const

export type ProcessStep = {
  readonly step: number
  readonly title: string
  readonly description: string
}

export const processSteps: readonly ProcessStep[] = [
  {
    step: 1,
    title: 'Discover',
    description:
      'We take time to understand your problem, your users, and your business goals deeply before writing a single line of code.',
  },
  {
    step: 2,
    title: 'Design',
    description:
      'We plan the solution end-to-end — architecture, data models, and UI — so nothing is improvised during build.',
  },
  {
    step: 3,
    title: 'Build',
    description:
      'We develop with complete transparency. Regular updates, working demos, and open communication throughout.',
  },
  {
    step: 4,
    title: 'Deliver',
    description:
      'We launch, document, and support. Delivery is not the end — it is the beginning of a long-term partnership.',
  },
] as const

export type WhyUsPoint = {
  readonly id: string
  readonly title: string
  readonly description: string
}

export const whyUsPoints: readonly WhyUsPoint[] = [
  {
    id: 'quality',
    title: 'Above All Quality',
    description:
      'No shortcuts. Every line of code is crafted with precision and purpose. Quality is our baseline, not our ceiling.',
  },
  {
    id: 'context',
    title: 'Built for Ethiopia & Beyond',
    description:
      'We understand the local context while building to global standards. Solutions that fit your reality.',
  },
  {
    id: 'ownership',
    title: 'You Own Your Code',
    description:
      'Full intellectual property ownership. No vendor lock-in. No ongoing dependency on us unless you choose it.',
  },
] as const

export const founderBio =
  'Hundaol — known as Hun — founded HUN TECH with one conviction: that Ethiopian businesses deserve software built to world-class standards. His name in Oromo means "Above All." That is not just a name — it became the standard.'
